import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DohopSearchState } from "../../types/dohop";
import { DEMO_AIRPORTS, encodeSearchParams } from "../../services/dohopApi";

interface DohopSearchFormProps {
    onSearch?: (searchParams: DohopSearchState) => void;
}

const DohopSearchForm: React.FC<DohopSearchFormProps> = ({ onSearch }) => {
    const navigate = useNavigate();
    const today = new Date();
    const defaultDate = new Date(today.setDate(today.getDate() + 14))
        .toISOString()
        .split("T")[0];

    const [formState, setFormState] = useState<DohopSearchState>({
        origin: "KEF",
        destination: "JFK",
        departureDate: defaultDate,
        adults: 1,
        children: 0,
        infants: 0,
    });

    const [showPassengerDropdown, setShowPassengerDropdown] = useState(false);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (onSearch) {
            onSearch(formState);
        }

        // Encode search params and navigate
        const encodedParams = encodeSearchParams(
            formState.origin,
            formState.destination,
            formState.departureDate,
            formState.adults,
            formState.children,
            formState.infants
        );

        navigate(`/dohop/results?search=${encodedParams}`);
    };

    const swapAirports = () => {
        setFormState((prev) => ({
            ...prev,
            origin: prev.destination,
            destination: prev.origin,
        }));
    };

    const totalPassengers =
        formState.adults + formState.children + formState.infants;

    const updatePassengers = (
        type: "adults" | "children" | "infants",
        delta: number
    ) => {
        setFormState((prev) => {
            const newValue = Math.max(
                type === "adults" ? 1 : 0,
                Math.min(9, prev[type] + delta)
            );
            return { ...prev, [type]: newValue };
        });
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="bg-white rounded-xl shadow-lg p-6 max-w-4xl mx-auto"
        >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                {/* Origin */}
                <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-dohop-text mb-1">
                        From
                    </label>
                    <select
                        value={formState.origin}
                        onChange={(e) =>
                            setFormState((prev) => ({
                                ...prev,
                                origin: e.target.value,
                            }))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dohop-blue focus:border-transparent text-dohop-text"
                    >
                        {DEMO_AIRPORTS.map((airport) => (
                            <option key={airport.code} value={airport.code}>
                                {airport.code} - {airport.city}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Swap button */}
                <div className="md:col-span-1 flex justify-center">
                    <button
                        type="button"
                        onClick={swapAirports}
                        className="p-2 bg-dohop-ice text-dohop-blue rounded-full hover:bg-dohop-blue hover:text-white transition-colors"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                            />
                        </svg>
                    </button>
                </div>

                {/* Destination */}
                <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-dohop-text mb-1">
                        To
                    </label>
                    <select
                        value={formState.destination}
                        onChange={(e) =>
                            setFormState((prev) => ({
                                ...prev,
                                destination: e.target.value,
                            }))
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dohop-blue focus:border-transparent text-dohop-text"
                    >
                        {DEMO_AIRPORTS.map((airport) => (
                            <option key={airport.code} value={airport.code}>
                                {airport.code} - {airport.city}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Date */}
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-dohop-text mb-1">
                        Departure
                    </label>
                    <input
                        type="date"
                        value={formState.departureDate}
                        onChange={(e) =>
                            setFormState((prev) => ({
                                ...prev,
                                departureDate: e.target.value,
                            }))
                        }
                        min={new Date().toISOString().split("T")[0]}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dohop-blue focus:border-transparent text-dohop-text"
                    />
                </div>

                {/* Passengers */}
                <div className="md:col-span-2 relative">
                    <label className="block text-sm font-medium text-dohop-text mb-1">
                        Passengers
                    </label>
                    <button
                        type="button"
                        onClick={() =>
                            setShowPassengerDropdown(!showPassengerDropdown)
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-left text-dohop-text bg-white focus:ring-2 focus:ring-dohop-blue focus:border-transparent"
                    >
                        {totalPassengers}{" "}
                        {totalPassengers === 1 ? "Passenger" : "Passengers"}
                    </button>

                    {showPassengerDropdown && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 p-4">
                            {/* Adults */}
                            <div className="flex items-center justify-between py-2">
                                <div>
                                    <div className="font-medium text-dohop-text">
                                        Adults
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        12+ years
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            updatePassengers("adults", -1)
                                        }
                                        disabled={formState.adults <= 1}
                                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-50"
                                    >
                                        -
                                    </button>
                                    <span className="w-8 text-center">
                                        {formState.adults}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            updatePassengers("adults", 1)
                                        }
                                        disabled={totalPassengers >= 9}
                                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-50"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>

                            {/* Children */}
                            <div className="flex items-center justify-between py-2 border-t">
                                <div>
                                    <div className="font-medium text-dohop-text">
                                        Children
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        2-11 years
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            updatePassengers("children", -1)
                                        }
                                        disabled={formState.children <= 0}
                                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-50"
                                    >
                                        -
                                    </button>
                                    <span className="w-8 text-center">
                                        {formState.children}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            updatePassengers("children", 1)
                                        }
                                        disabled={totalPassengers >= 9}
                                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-50"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>

                            {/* Infants */}
                            <div className="flex items-center justify-between py-2 border-t">
                                <div>
                                    <div className="font-medium text-dohop-text">
                                        Infants
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        Under 2 years
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            updatePassengers("infants", -1)
                                        }
                                        disabled={formState.infants <= 0}
                                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-50"
                                    >
                                        -
                                    </button>
                                    <span className="w-8 text-center">
                                        {formState.infants}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            updatePassengers("infants", 1)
                                        }
                                        disabled={
                                            formState.infants >=
                                                formState.adults ||
                                            totalPassengers >= 9
                                        }
                                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-50"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => setShowPassengerDropdown(false)}
                                className="w-full mt-3 py-2 bg-dohop-blue text-white rounded-lg font-medium"
                            >
                                Done
                            </button>
                        </div>
                    )}
                </div>

                {/* Search button */}
                <div className="md:col-span-1">
                    <button
                        type="submit"
                        className="w-full px-6 py-3 bg-dohop-blue text-white rounded-lg font-semibold hover:bg-dohop-blue-dark transition-colors flex items-center justify-center"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        </form>
    );
};

export default DohopSearchForm;
